/**
 * Document Templates Page
 * Create and manage document templates with placeholders
 * Accessible at /app/documents/templates
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Drawer } from '@/components/campus/Drawer';
import { toast } from 'sonner';
import { PageHeader, EmptyState, BadgePill } from '@/components/campus';
import {
  FileText,
  Plus,
  Edit,
  Trash2,
  Eye,
  Copy,
  Loader2,
  FileCode,
  ArrowLeft,
  Info,
  Sparkles,
  Download,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Document type options
const DOC_TYPES = [
  { value: 'membership_agreement', label: 'Membership Agreement' },
  { value: 'liability_waiver', label: 'Liability Waiver' },
  { value: 'code_of_conduct', label: 'Code of Conduct' },
  { value: 'financial_agreement', label: 'Financial Agreement' },
  { value: 'other', label: 'Other' },
];

// Available placeholders
const AVAILABLE_PLACEHOLDERS = [
  { key: 'member_name', label: 'Member Name', description: 'Full name of the member' },
  { key: 'member_email', label: 'Member Email', description: 'Email address of the member' },
  { key: 'organization_name', label: 'Organization Name', description: 'Name of your chapter' },
  { key: 'date', label: 'Current Date', description: 'Today\'s date (e.g., January 15, 2026)' },
  { key: 'year', label: 'Current Year', description: 'Current year (e.g., 2026)' },
];

// ==================== TEMPLATE CARD ====================
const TemplateCard = ({ template, onEdit, onDelete, onPreview, onGenerate }) => (
  <div className="p-5 bg-graphite/50 rounded-xl border border-white/[0.06] hover:border-electric-ice-blue/30 transition-all">
    <div className="flex items-start justify-between">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-electric-ice-blue/10 flex items-center justify-center">
          <FileCode className="w-5 h-5 text-electric-ice-blue" />
        </div>
        <div>
          <h3 className="font-semibold text-cloud-white">{template.name}</h3>
          <BadgePill variant="default" size="sm" className="mt-1">
            {DOC_TYPES.find(t => t.value === template.doc_type)?.label || template.doc_type}
          </BadgePill>
        </div>
      </div>
    </div>

    {/* Placeholders */}
    {template.placeholder_fields?.length > 0 && (
      <div className="mt-4 flex flex-wrap gap-1.5">
        {template.placeholder_fields.slice(0, 4).map((field) => (
          <span key={field.key} className="px-2 py-0.5 bg-white/[0.05] rounded text-xs text-soft-silver">
            {`{{${field.key}}}`}
          </span>
        ))}
        {template.placeholder_fields.length > 4 && (
          <span className="px-2 py-0.5 bg-white/[0.05] rounded text-xs text-muted-text">
            +{template.placeholder_fields.length - 4} more
          </span>
        )}
      </div>
    )}

    {/* Actions */}
    <div className="mt-4 flex gap-2">
      <Button
        variant="outline"
        size="sm"
        onClick={() => onPreview(template)}
        className="border-white/[0.08] text-soft-silver hover:text-cloud-white"
      >
        <Eye className="w-4 h-4 mr-1" />
        Preview
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={() => onGenerate(template)}
        className="border-cool-mint/30 text-cool-mint hover:bg-cool-mint/10"
      >
        <Sparkles className="w-4 h-4 mr-1" />
        Generate
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onEdit(template)}
        className="text-soft-silver hover:text-cloud-white"
      >
        <Edit className="w-4 h-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onDelete(template)}
        className="text-signal-red hover:bg-signal-red/10"
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>

    <p className="mt-3 text-xs text-muted-text">
      Created by {template.created_by_name} • {new Date(template.created_at).toLocaleDateString()}
    </p>
  </div>
);

// ==================== CREATE/EDIT DRAWER ====================
const TemplateDrawer = ({ template, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    doc_type: 'other',
    template_content: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name || '',
        doc_type: template.doc_type || 'other',
        template_content: template.template_content || '',
      });
    } else {
      setFormData({
        name: '',
        doc_type: 'other',
        template_content: '',
      });
    }
  }, [template]);

  const insertPlaceholder = (key) => {
    const placeholder = `{{${key}}}`;
    setFormData(prev => ({
      ...prev,
      template_content: prev.template_content + placeholder,
    }));
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    if (!formData.template_content.trim()) {
      toast.error('Please enter template content');
      return;
    }

    setSaving(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      // Error handled by parent
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={template ? 'Edit Template' : 'Create Template'}
      width="xl"
    >
      <div className="space-y-6">
        {/* Name */}
        <div className="space-y-2">
          <Label className="text-soft-silver">Template Name</Label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Membership Agreement"
            className="bg-obsidian border-white/[0.08] text-cloud-white"
          />
        </div>

        {/* Doc Type */}
        <div className="space-y-2">
          <Label className="text-soft-silver">Document Type</Label>
          <Select
            value={formData.doc_type}
            onValueChange={(v) => setFormData({ ...formData, doc_type: v })}
          >
            <SelectTrigger className="bg-obsidian border-white/[0.08] text-cloud-white">
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent className="bg-graphite border-white/[0.08]">
              {DOC_TYPES.map((type) => (
                <SelectItem key={type.value} value={type.value} className="text-cloud-white">
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Placeholder Buttons */}
        <div className="space-y-2">
          <Label className="text-soft-silver">Insert Placeholder</Label>
          <div className="flex flex-wrap gap-2">
            {AVAILABLE_PLACEHOLDERS.map((ph) => (
              <Button
                key={ph.key}
                type="button"
                variant="outline"
                size="sm"
                onClick={() => insertPlaceholder(ph.key)}
                className="border-electric-ice-blue/30 text-electric-ice-blue hover:bg-electric-ice-blue/10"
              >
                {`{{${ph.key}}}`}
              </Button>
            ))}
          </div>
          <p className="text-xs text-muted-text">
            Click to insert. Placeholders will be auto-filled when generating documents.
          </p>
        </div>

        {/* Template Content */}
        <div className="space-y-2">
          <Label className="text-soft-silver">Template Content</Label>
          <Textarea
            value={formData.template_content}
            onChange={(e) => setFormData({ ...formData, template_content: e.target.value })}
            placeholder={`Enter your document template here...\n\nExample:\nI, {{member_name}}, hereby agree to the terms and conditions of {{organization_name}} as of {{date}}.`}
            rows={12}
            className="bg-obsidian border-white/[0.08] text-cloud-white font-mono text-sm resize-none"
          />
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t border-white/[0.06]">
          <Button variant="outline" onClick={onClose} className="flex-1 border-white/[0.08]">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 bg-electric-ice-blue text-white"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {template ? 'Save Changes' : 'Create Template'}
          </Button>
        </div>
      </div>
    </Drawer>
  );
};

// ==================== PREVIEW DRAWER ====================
const PreviewDrawer = ({ template, isOpen, onClose, session, organizationId }) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchPreview = async () => {
      if (!template || !isOpen) return;
      setLoading(true);
      try {
        const res = await fetch(
          `${API_URL}/api/document-templates/${template.id}/preview`,
          { headers: { Authorization: `Bearer ${session.access_token}` } }
        );
        if (res.ok) {
          setPreview(await res.json());
        }
      } catch (error) {
        console.error('Preview error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPreview();
  }, [template, isOpen, session?.access_token]);

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title="Template Preview" width="xl">
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 text-electric-ice-blue animate-spin" />
        </div>
      ) : preview ? (
        <div className="space-y-6">
          {/* Sample Values */}
          <div className="p-4 bg-soft-amber/10 rounded-lg border border-soft-amber/20">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 text-soft-amber mt-0.5" />
              <div>
                <p className="text-sm text-soft-amber font-medium">Preview with Sample Data</p>
                <p className="text-xs text-soft-silver mt-1">
                  Placeholders filled with: {Object.entries(preview.sample_values).map(([k, v]) => `${k}="${v}"`).join(', ')}
                </p>
              </div>
            </div>
          </div>

          {/* Preview Content */}
          <div className="p-6 bg-white rounded-lg">
            <pre className="whitespace-pre-wrap text-sm text-gray-900 font-serif leading-relaxed">
              {preview.preview_content}
            </pre>
          </div>
        </div>
      ) : (
        <EmptyState icon="alert-circle" title="Failed to Load Preview" />
      )}
    </Drawer>
  );
};

// ==================== GENERATE DRAWER ====================
const GenerateDrawer = ({ template, isOpen, onClose, onGenerate }) => {
  const [generating, setGenerating] = useState(false);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await onGenerate(template.id);
      onClose();
    } catch (error) {
      // Error handled by parent
    } finally {
      setGenerating(false);
    }
  };

  if (!template) return null;

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title="Generate Document" width="lg">
      <div className="space-y-6">
        <div className="p-4 bg-obsidian/50 rounded-lg border border-white/[0.06]">
          <h3 className="font-medium text-cloud-white">{template.name}</h3>
          <p className="text-sm text-soft-silver mt-1">
            This will create a new document from this template with auto-filled placeholders.
          </p>
        </div>

        <div className="p-4 bg-cool-mint/10 rounded-lg border border-cool-mint/20">
          <p className="text-sm text-cool-mint">
            The following will be auto-filled: organization name, current date, and year.
            Member-specific fields will be filled when the document is assigned.
          </p>
        </div>

        <div className="flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1 border-white/[0.08]">
            Cancel
          </Button>
          <Button
            onClick={handleGenerate}
            disabled={generating}
            className="flex-1 bg-cool-mint text-white hover:bg-cool-mint/90"
          >
            {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            Generate Document
          </Button>
        </div>
      </div>
    </Drawer>
  );
};

// ==================== MAIN PAGE ====================
export const DocumentTemplates = () => {
  const { organization, session } = useAuth();
  const { hasOrgRole } = usePermissions();
  const navigate = useNavigate();

  const canManage = hasOrgRole(['owner', 'admin']);

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [generateTemplate, setGenerateTemplate] = useState(null);

  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    if (!organization?.id || !session?.access_token) {
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(
        `${API_URL}/api/document-templates/list/${organization.id}`,
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );

      if (res.ok) {
        const data = await res.json();
        setTemplates(data.templates || []);
        if (data.error) {
          setError(data.error);
        }
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    } finally {
      setLoading(false);
    }
  }, [organization?.id, session?.access_token]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Create template
  const handleCreate = async (data) => {
    const res = await fetch(
      `${API_URL}/api/document-templates/create/${organization.id}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(data),
      }
    );

    if (res.ok) {
      toast.success('Template created');
      fetchTemplates();
    } else {
      const err = await res.json();
      toast.error(err.detail || 'Failed to create template');
      throw new Error(err.detail);
    }
  };

  // Update template
  const handleUpdate = async (data) => {
    const res = await fetch(
      `${API_URL}/api/document-templates/${editingTemplate.id}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(data),
      }
    );

    if (res.ok) {
      toast.success('Template updated');
      fetchTemplates();
      setEditingTemplate(null);
    } else {
      toast.error('Failed to update template');
      throw new Error('Failed to update');
    }
  };

  // Delete template
  const handleDelete = async (template) => {
    if (!confirm(`Delete template "${template.name}"? This cannot be undone.`)) return;

    const res = await fetch(
      `${API_URL}/api/document-templates/${template.id}`,
      {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${session.access_token}` },
      }
    );

    if (res.ok) {
      toast.success('Template deleted');
      fetchTemplates();
    } else {
      toast.error('Failed to delete template');
    }
  };

  // Generate document
  const handleGenerate = async (templateId) => {
    const res = await fetch(
      `${API_URL}/api/document-templates/${templateId}/generate`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ template_id: templateId }),
      }
    );

    if (res.ok) {
      const data = await res.json();
      toast.success('Document generated! Check the Documents page.');
      navigate('/app/documents');
    } else {
      const err = await res.json();
      toast.error(err.detail || 'Failed to generate document');
      throw new Error(err.detail);
    }
  };

  if (!canManage) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <EmptyState icon="lock" title="Access Restricted" description="Only owners and admins can manage document templates." />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-electric-ice-blue animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="document-templates-page">
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => navigate('/app/documents')}
        className="text-soft-silver hover:text-cloud-white -ml-2"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Documents
      </Button>

      <div className="flex items-center justify-between">
        <PageHeader
          title="Document Templates"
          subtitle="Create reusable templates with auto-fill placeholders"
          icon={FileCode}
        />
        <Button
          onClick={() => setIsCreateOpen(true)}
          className="bg-electric-ice-blue text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Template
        </Button>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="p-4 bg-signal-red/10 rounded-lg border border-signal-red/20">
          <p className="text-sm text-signal-red">{error}</p>
          <p className="text-xs text-soft-silver mt-1">
            Run this SQL in Supabase: CREATE TABLE IF NOT EXISTS document_templates (...)
          </p>
        </div>
      )}

      {/* Info Banner */}
      <div className="p-4 bg-electric-ice-blue/5 rounded-lg border border-electric-ice-blue/20">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-electric-ice-blue flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="text-cloud-white font-medium">How Templates Work</p>
            <p className="text-soft-silver mt-1">
              Create templates with placeholders like <code className="text-electric-ice-blue">{'{{member_name}}'}</code> that auto-fill when generating documents.
              Generated documents are automatically added to your Document Vault for signatures.
            </p>
          </div>
        </div>
      </div>

      {/* Templates Grid */}
      {templates.length === 0 ? (
        <EmptyState
          icon="file-text"
          title="No Templates Yet"
          description="Create your first document template to streamline document creation."
          actionLabel="Create Template"
          onAction={() => setIsCreateOpen(true)}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              onEdit={setEditingTemplate}
              onDelete={handleDelete}
              onPreview={setPreviewTemplate}
              onGenerate={setGenerateTemplate}
            />
          ))}
        </div>
      )}

      {/* Drawers */}
      <TemplateDrawer
        template={null}
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onSave={handleCreate}
      />

      <TemplateDrawer
        template={editingTemplate}
        isOpen={!!editingTemplate}
        onClose={() => setEditingTemplate(null)}
        onSave={handleUpdate}
      />

      <PreviewDrawer
        template={previewTemplate}
        isOpen={!!previewTemplate}
        onClose={() => setPreviewTemplate(null)}
        session={session}
        organizationId={organization?.id}
      />

      <GenerateDrawer
        template={generateTemplate}
        isOpen={!!generateTemplate}
        onClose={() => setGenerateTemplate(null)}
        onGenerate={handleGenerate}
      />
    </div>
  );
};

export default DocumentTemplates;
